import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  DeveloperMode as DeveloperModeIcon,
  Public as PublicIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import axios from 'axios';

const DeploymentDetail = ({ agentId, tenantId }) => {
  // State for editing agent domain
  const [editingDomain, setEditingDomain] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [domainSnackbar, setDomainSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [aiModeExportDialogOpen, setAiModeExportDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copySuccessMessage, setCopySuccessMessage] = useState('Deployment code copied to clipboard!');
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [agent, setAgent] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'verified', 'pending', 'testing'
  const [loading, setLoading] = useState(false);
  const [encryptedAgentId, setEncryptedAgentId] = useState(null);
  const [encryptedTenantId, setEncryptedTenantId] = useState(null);
  const [testMenuAnchor, setTestMenuAnchor] = useState(null);

  useEffect(() => {
    if (agentId) {
      fetchAgentDetails();
    }
  }, [agentId]);

  // Helper function to parse domains from input (comma, semicolon, or space delimited)
  const parseDomains = (input) => {
    if (!input || typeof input !== 'string') return [];
    // Split by comma, semicolon, or whitespace, then filter out empty strings and trim
    return input
      .split(/[,;\s]+/)
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
  };

  // Helper function to format domains array back to string (comma-separated)
  const formatDomainsToString = (domains) => {
    if (!domains || domains.length === 0) return '';
    return domains.join(', ');
  };

  // Get domains array from agent
  const getDomains = () => {
    if (!agent?.agentDomain) return [];
    return parseDomains(agent.agentDomain);
  };

  // Keep domainInput in sync with agent
  useEffect(() => {
    if (agent && typeof agent.agentDomain === 'string') {
      setDomainInput(agent.agentDomain);
    }
  }, [agent]);

  // Auto-verify on agent load if domain exists
  useEffect(() => {
    if (agent?.agentDomain) {
      // Check if we have stored verification status
      const stored = localStorage.getItem(`agent-${agentId}-verification`);
      if (stored) {
        setVerificationStatus(stored);
      }
    }
  }, [agent, agentId]);

  const fetchAgentDetails = async () => {
    try {
      const response = await axios.post('/agents/getAgentDetail', null, {
        params: { agentId: agentId }
      });
      setAgent(response.data);
    } catch (error) {
      console.error('Error fetching agent details:', error);
    }
  };

  const fetchEncryptedIds = async () => {
    try {
      const response = await axios.post('/agents/encrypt-ids', null, {
        params: { agentId: agentId }
      });
      setEncryptedAgentId(response.data.encryptedAgentId);
      setEncryptedTenantId(response.data.encryptedTenantId);
    } catch (error) {
      console.error('Error fetching encrypted IDs:', error);
      // Fallback to empty strings if encryption fails
      setEncryptedAgentId('');
      setEncryptedTenantId('');
    }
  };

  const generateExportCode = () => {
    if (!agent || !encryptedAgentId || !encryptedTenantId) return '';

    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const widgetAppBase = `${window.location.origin}${publicBase}`.replace(/\/$/, '');
    const scriptSrc = `${widgetAppBase}/widget.js`;

    const deploymentCode = `<script>
  window.slickbotsConfig = {
    agent: '${encryptedAgentId}',
    tenant: '${encryptedTenantId}',
    widgetUrl: '${widgetAppBase}',
  };
</script>
<script src="${scriptSrc}" async></script>`;

    return deploymentCode;
  };

  const AI_MODE_CONTAINER_ID = 'ai-mode-root';

  const generateAiModeExportCode = () => {
    if (!agent || !encryptedAgentId || !encryptedTenantId) return '';

    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const widgetAppBase = `${window.location.origin}${publicBase}`.replace(/\/$/, '');
    const scriptSrc = `${widgetAppBase}/widget.js`;

    return `<!-- 1. Place this container where the AI chat should appear -->
<div id="${AI_MODE_CONTAINER_ID}" aria-label="AI chat"></div>

<!-- 2. Add this CSS in a <style> tag (adjust dimensions as needed) -->
<style>
  #${AI_MODE_CONTAINER_ID} {
    position: relative;
    width: 100%;
    height: clamp(650px, 70vh, 850px);
    max-width: 1200px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  @media (max-width: 767px) {
  #ai-mode-root {
    height: calc(100dvh - 80px);
    min-height: 550px;
    max-height: none;
  }
}
</style>

<!-- 3. Add configuration and widget script -->
<script>
  window.slickbotsConfig = {
    agent: '${encryptedAgentId}',
    tenant: '${encryptedTenantId}',
    widgetUrl: '${widgetAppBase}',
    embedContainer: '#${AI_MODE_CONTAINER_ID}',
    embedLayout: 'full',
    transparentBackground: true,
  };
</script>
<script src="${scriptSrc}" async></script>`;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generateExportCode())
      .then(() => {
        setCopySuccessMessage('Deployment code copied to clipboard!');
        setCopySuccess(true);
      })
      .catch(err => console.error('Failed to copy: ', err));
  };

  const handleCopyAiModeCode = () => {
    navigator.clipboard.writeText(generateAiModeExportCode())
      .then(() => {
        setCopySuccessMessage('AI Mode code copied to clipboard!');
        setCopySuccess(true);
      })
      .catch(err => console.error('Failed to copy: ', err));
  };

  const verifyWidgetDeployment = async () => {
    const domains = getDomains();
    if (domains.length === 0) return;

    setVerificationStatus('testing');
    setLoading(true);

    let verifiedCount = 0;
    let failedDomains = [];

    try {
      // Verify each domain
      const verificationPromises = domains.map(async (domain) => {
        try {
          // Enhanced verification logic
          if (domain.includes('localhost')) {
            // For localhost, check if the local server is running and WebSocket is working
            try {
              // Test HTTP connection first
              const httpResponse = await fetch(domain, {
                method: 'HEAD',
                mode: 'no-cors',
                timeout: 5000
              });

              // Test WebSocket connection if HTTP is working
              const wsUrl = domain.replace('http:', 'ws:').replace('https:', 'wss:');
              const testWs = new WebSocket(`${wsUrl}/ws/public-chat/agentId=${agentId}/${tenantId}/1`);

              return new Promise((resolve) => {
                testWs.onopen = () => {
                  console.log(`✅ WebSocket connection verified for ${domain}`);
                  testWs.close();
                  resolve(true);
                };

                testWs.onerror = () => {
                  console.log(`⚠️ WebSocket connection failed for ${domain}, but HTTP is working`);
                  resolve(true); // Still mark as verified if HTTP works
                };

                // Timeout the WebSocket test after 3 seconds
                setTimeout(() => {
                  if (testWs.readyState === WebSocket.CONNECTING) {
                    testWs.close();
                  }
                  resolve(true); // Assume working if HTTP responded
                }, 3000);
              });
            } catch (error) {
              console.log(`⚠️ Local server verification skipped for ${domain} due to CORS`);
              return true; // Assume working for localhost
            }
          } else {
            // For production domains, use the backend verification endpoint
            try {
              const response = await axios.post('/agents/verify-deployment', {
                agentId: agentId,
                domain: domain
              });
              return response.data.verified;
            } catch (error) {
              console.error(`Verification failed for ${domain}:`, error);
              return false;
            }
          }
        } catch (error) {
          console.error(`Verification error for ${domain}:`, error);
          return false;
        }
      });

      const results = await Promise.all(verificationPromises);

      results.forEach((verified, index) => {
        if (verified) {
          verifiedCount++;
        } else {
          failedDomains.push(domains[index]);
        }
      });

      // Set overall verification status
      if (verifiedCount === domains.length) {
        setVerificationStatus('verified');
        localStorage.setItem(`agent-${agentId}-verification`, 'verified');
        setVerificationSuccess(true);
      } else if (verifiedCount > 0) {
        setVerificationStatus('pending');
        localStorage.setItem(`agent-${agentId}-verification`, 'pending');
        setDomainSnackbar({
          open: true,
          message: `Some domains failed verification: ${failedDomains.join(', ')}`,
          severity: 'warning'
        });
      } else {
        setVerificationStatus('pending');
        localStorage.setItem(`agent-${agentId}-verification`, 'pending');
        setDomainSnackbar({
          open: true,
          message: 'Deployment code not detected at the specified domains. Please ensure you have deployed the widget code and try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('pending');
      setDomainSnackbar({
        open: true,
        message: 'Verification failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestAgent = (domain = null) => {
    const domains = getDomains();
    if (domains.length === 0) return;

    if (domain) {
      // Open specific domain
      window.open(domain, '_blank');
    } else if (domains.length === 1) {
      // Single domain - open directly
      window.open(domains[0], '_blank');
      verifyWidgetDeployment();
    } else {
      // Multiple domains - show menu
      setTestMenuAnchor(document.getElementById('test-agent-button'));
    }
  };

  const handleTestMenuClose = (domain = null) => {
    setTestMenuAnchor(null);
    if (domain) {
      window.open(domain, '_blank');
    }
  };

  const getVerificationChip = () => {
    if (verificationStatus === 'verified') {
      return (
        <Chip
          icon={<VerifiedIcon />}
          label="Verified"
          color="success"
          variant="outlined"
        />
      );
    }
    if (verificationStatus === 'testing') {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Testing... ⏳"
          color="info"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<WarningIcon />}
        label="Pending Verification"
        color="warning"
        variant="outlined"
      />
    );
  };

  return (

    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Deployment Details
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        View and manage deployment settings for your WebSocket-powered chat agent.
      </Typography>

      {/* Deployment Domain Section - Stacked and Centered */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom align="center">
          Deployment Domain
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
          {/* Domain Input Field */}
          {editingDomain ? (
            <>
              <TextField
                label="Agent Domains"
                value={domainInput || ''}
                onChange={e => setDomainInput(e.target.value)}
                size="small"
                fullWidth
                sx={{ maxWidth: 400 }}
                multiline
                rows={3}
                helperText="Enter multiple domains separated by commas, semicolons, or spaces"
                placeholder="example.com, www.example.com; another.com"
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    if (!domainInput) return;
                    setLoading(true);
                    try {
                      // Parse and validate domains
                      const domains = parseDomains(domainInput);
                      if (domains.length === 0) {
                        setDomainSnackbar({ open: true, message: 'Please enter at least one valid domain.', severity: 'error' });
                        setLoading(false);
                        return;
                      }

                      // Save domains to backend (as comma-separated string)
                      const domainsString = formatDomainsToString(domains);
                      await axios.post('/agents/saveAgent', {
                        ...agent,
                        agentDomain: domainsString
                      });
                      setAgent(prev => ({ ...prev, agentDomain: domainsString }));
                      setEditingDomain(false);
                      setDomainSnackbar({
                        open: true,
                        message: `${domains.length} domain${domains.length > 1 ? 's' : ''} updated!`,
                        severity: 'success'
                      });
                    } catch (err) {
                      const d = err?.response?.data;
                      const apiMsg = typeof d === 'string' ? d : d?.message;
                      setDomainSnackbar({
                        open: true,
                        message: apiMsg || 'Failed to update domains.',
                        severity: 'error'
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !domainInput}
                >
                  Save
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setEditingDomain(false);
                    setDomainInput(agent?.agentDomain || '');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              {getDomains().length > 0 ? (
                <Box sx={{ width: '100%', maxWidth: 400 }}>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 56,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {getDomains().map((domain, index) => (
                      <Chip
                        key={index}
                        label={domain}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <TextField
                  label="Agent Domain"
                  value=""
                  size="small"
                  fullWidth
                  sx={{ maxWidth: 400 }}
                  disabled
                  placeholder="No domains configured"
                />
              )}
              {!agent?.agentDomain ? (
                <Button
                  variant="contained"
                  onClick={() => setEditingDomain(true)}
                  size="large"
                  sx={{ width: '100%', maxWidth: 400 }}
                >
                  Add Domain
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', width: '100%', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setEditingDomain(true)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    Edit Domains
                  </Button>
                  {getVerificationChip()}
                </Box>
              )}
            </>
          )}

          {/* Action Buttons - Stacked and Centered */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%', mt: 2 }}>
            {agent?.agentDomain && (
              <Button
                variant="outlined"
                startIcon={<VerifiedIcon />}
                onClick={verifyWidgetDeployment}
                disabled={loading}
                color={verificationStatus === 'verified' ? 'success' : 'primary'}
                sx={{ width: 400, maxWidth: '100%', mx: 'auto' }}
              >
                {loading ? 'VERIFYING...' : 'VERIFY DEPLOYMENT'}
              </Button>
            )}
            {agent?.agentDomain && (
              <>
                <Button
                  id="test-agent-button"
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  endIcon={getDomains().length > 1 ? <ArrowDropDownIcon /> : null}
                  onClick={() => handleTestAgent()}
                  disabled={loading}
                  sx={{ width: 400, maxWidth: '100%', mx: 'auto' }}
                >
                  TEST AGENT{getDomains().length > 1 ? ` (${getDomains().length} domains)` : ''}
                </Button>
                {getDomains().length > 1 && (
                  <Menu
                    anchorEl={testMenuAnchor}
                    open={Boolean(testMenuAnchor)}
                    onClose={() => handleTestMenuClose()}
                  >
                    {getDomains().map((domain, index) => (
                      <MenuItem key={index} onClick={() => handleTestMenuClose(domain)}>
                        <LaunchIcon sx={{ mr: 1, fontSize: 18 }} />
                        {domain}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </>
            )}
            <Button
              variant="contained"
              onClick={async () => {
                setExportDialogOpen(true);
                await fetchEncryptedIds();
              }}
              disabled={!agent}
              size="large"
              sx={{ width: 400, maxWidth: '100%', mx: 'auto' }}
            >
              EXPORT DEPLOYMENT CODE
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setAiModeExportDialogOpen(true);
                await fetchEncryptedIds();
              }}
              disabled={!agent}
              size="large"
              sx={{ width: 400, maxWidth: '100%', mx: 'auto' }}
            >
              EXPORT AI MODE CODE
            </Button>
          </Box>
        </Box>
        <Snackbar
          open={domainSnackbar.open}
          autoHideDuration={3000}
          onClose={() => setDomainSnackbar({ ...domainSnackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setDomainSnackbar({ ...domainSnackbar, open: false })}
            severity={domainSnackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {domainSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Info alert removed, now handled by editable field above */}

      {/* Export Code Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => {
          setExportDialogOpen(false);
          setEncryptedAgentId(null);
          setEncryptedTenantId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Export Deployment Code
          <IconButton
            aria-label="close"
            onClick={() => setExportDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Copy this code and paste it into your website's HTML to embed the WebSocket-powered chat widget.
          </Typography>



          <Box sx={{ position: 'relative', mt: 2 }}>
            <TextField
              multiline
              fullWidth
              rows={8}
              value={generateExportCode()}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace'
                }
              }}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton
                onClick={handleCopyCode}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export AI Mode Code Dialog */}
      <Dialog
        open={aiModeExportDialogOpen}
        onClose={() => {
          setAiModeExportDialogOpen(false);
          setEncryptedAgentId(null);
          setEncryptedTenantId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Export AI Mode Code
          <IconButton
            aria-label="close"
            onClick={() => setAiModeExportDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Copy this code to embed a full-page AI chat experience on your site. The widget fills the container you provide and supports a transparent background.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Setup steps
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
              <li>
                Add a container element where the chat should render:
                <Box
                  component="code"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    mb: 1,
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {`<div id="${AI_MODE_CONTAINER_ID}" aria-label="AI chat"></div>`}
                </Box>
              </li>
              <li>
                Add CSS for the container (adjust width, height, and max dimensions for your layout). The snippet below includes a recommended starting point in a <code>&lt;style&gt;</code> tag.
              </li>
              <li>
                Paste the configuration script with <code>embedContainer</code>, <code>embedLayout: &apos;full&apos;</code>, and <code>transparentBackground: true</code>, then load <code>widget.js</code>.
              </li>
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            If you change the container id, update it in the HTML, CSS selector, and <code>embedContainer</code> value so they all match.
          </Typography>

          <Box sx={{ position: 'relative', mt: 1 }}>
            <TextField
              multiline
              fullWidth
              rows={18}
              value={generateAiModeExportCode()}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace'
                }
              }}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton
                onClick={handleCopyAiModeCode}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiModeExportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success">
          {copySuccessMessage}
        </Alert>
      </Snackbar>

      {/* Verification Success Snackbar */}
      <Snackbar
        open={verificationSuccess}
        autoHideDuration={4000}
        onClose={() => setVerificationSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setVerificationSuccess(false)} severity="success">
          ✅ WebSocket deployment verified successfully! Your chat widget is ready.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeploymentDetail;
